(() => {
  const WRAPPER_SELECTOR = '[data-description-wrapper]';
  const DEFAULT_COLLAPSED_HEIGHT = 0;
  const HEIGHT_BUFFER = 8;
  const MAX_EXPANDED_HEIGHT_RATIO = 0.7; // 70% of viewport height
  let managedWrappers = [];
  let resizeRaf;

  const getMaxExpandedHeight = () => window.innerHeight * MAX_EXPANDED_HEIGHT_RATIO;

  const applyMaxHeight = (element, value) => {
    element.style.maxHeight = typeof value === 'number' ? `${value}px` : value;
  };

  const setToggleLabel = (entry, expanded) => {
    const more = entry.toggle.dataset.showMore || 'Show details';
    const less = entry.toggle.dataset.showLess || 'Hide details';
    entry.toggleText.textContent = expanded ? less : more;
  };

  const unlockOnExpand = (entry) => {
    const handleTransitionEnd = (event) => {
      if (event.target !== entry.content) return;
      if (entry.wrapper.dataset.descriptionState === 'expanded') {
        // Set final constrained height with scrolling capability
        const maxExpandedHeight = getMaxExpandedHeight();
        const contentHeight = entry.content.scrollHeight;
        const finalHeight = Math.min(contentHeight, maxExpandedHeight);

        applyMaxHeight(entry.content, finalHeight);
        entry.content.style.overflowY = contentHeight > maxExpandedHeight ? 'auto' : 'visible';
      }
      entry.content.removeEventListener('transitionend', handleTransitionEnd);
    };

    entry.content.addEventListener('transitionend', handleTransitionEnd);
  };

  const collapse = (entry) => {
    entry.wrapper.dataset.descriptionState = 'collapsed';
    entry.toggle.setAttribute('aria-expanded', 'false');
    setToggleLabel(entry, false);

    // Reset overflow for collapsed state
    entry.content.style.overflowY = 'hidden';

    const currentHeight =
      entry.content.style.maxHeight === 'none' || entry.content.style.maxHeight === ''
        ? entry.content.scrollHeight
        : parseFloat(entry.content.style.maxHeight);
    applyMaxHeight(entry.content, currentHeight);
    requestAnimationFrame(() => {
      applyMaxHeight(entry.content, entry.collapsedHeight);
    });
  };

  const expand = (entry) => {
    entry.wrapper.dataset.descriptionState = 'expanded';
    entry.toggle.setAttribute('aria-expanded', 'true');
    setToggleLabel(entry, true);

    // Calculate the target height: either full content or 70vh, whichever is smaller
    const maxExpandedHeight = getMaxExpandedHeight();
    const contentHeight = entry.content.scrollHeight;
    const targetHeight = Math.min(contentHeight, maxExpandedHeight);

    applyMaxHeight(entry.content, targetHeight);
    unlockOnExpand(entry);
  };

  const handleToggle = (entry) => {
    if (entry.wrapper.dataset.descriptionState === 'expanded') {
      collapse(entry);
      return;
    }

    expand(entry);
  };

  const setupWrapper = (wrapper) => {
    if (wrapper.dataset.descriptionInitialized === 'true') return;

    const content = wrapper.querySelector('[data-description-content]');
    const toggle = wrapper.querySelector('[data-description-toggle]');
    const toggleText = toggle ? toggle.querySelector('[data-toggle-text]') : null;

    wrapper.dataset.descriptionInitialized = 'true';

    if (!content || !toggle || !toggleText) {
      return;
    }

    const configuredHeight = parseInt(wrapper.dataset.collapsedHeight || '', 10);
    const collapsedHeight = Number.isNaN(configuredHeight)
      ? DEFAULT_COLLAPSED_HEIGHT
      : configuredHeight;
    const needsToggle = content.scrollHeight > collapsedHeight + HEIGHT_BUFFER;

    if (!needsToggle) {
      toggle.hidden = true;
      wrapper.dataset.descriptionState = 'expanded';
      return;
    }

    wrapper.dataset.descriptionState = 'collapsed';
    applyMaxHeight(content, collapsedHeight);
    toggle.hidden = false;
    toggle.setAttribute('aria-expanded', 'false');
    setToggleLabel({ toggle, toggleText }, false);

    const entry = { wrapper, content, toggle, toggleText, collapsedHeight };
    entry.listener = () => handleToggle(entry);
    toggle.addEventListener('click', entry.listener);
    managedWrappers.push(entry);
  };

  const initDescriptions = (scope = document) => {
    scope.querySelectorAll(WRAPPER_SELECTOR).forEach(setupWrapper);
  };

  const cleanupManagedWrappers = (container) => {
    managedWrappers = managedWrappers.filter((entry) => {
      if (!container.contains(entry.wrapper)) {
        return true;
      }
      entry.toggle.removeEventListener('click', entry.listener);
      return false;
    });
  };

  const updateHeights = () => {
    managedWrappers = managedWrappers.filter((entry) => {
      if (!document.body.contains(entry.wrapper)) {
        entry.toggle.removeEventListener('click', entry.listener);
        return false;
      }

      if (entry.wrapper.dataset.descriptionState === 'expanded') {
        // Recalculate expanded height on resize
        const maxExpandedHeight = getMaxExpandedHeight();
        const contentHeight = entry.content.scrollHeight;
        const targetHeight = Math.min(contentHeight, maxExpandedHeight);

        if (entry.content.style.maxHeight !== 'none') {
          applyMaxHeight(entry.content, targetHeight);
          entry.content.style.overflowY = contentHeight > maxExpandedHeight ? 'auto' : 'visible';
        }
      } else {
        applyMaxHeight(entry.content, entry.collapsedHeight);
        entry.content.style.overflowY = 'hidden';
      }

      return true;
    });
  };

  const requestHeightUpdate = () => {
    if (resizeRaf) {
      cancelAnimationFrame(resizeRaf);
    }
    resizeRaf = requestAnimationFrame(updateHeights);
  };

  const onReady = () => {
    initDescriptions();
    window.addEventListener('resize', requestHeightUpdate);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initDescriptions(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupManagedWrappers(event.target);
  });
})();
